import { useState, type ComponentPropsWithoutRef } from "react";

import { Controller, type SubmitHandler, type UseFormReturn } from "react-hook-form";

import { RoomType, type Building, type Floor } from "@eisox/api/core-api";
import { ActionButton, Button, cn, Radio, Select, Slider, Switch, TextField } from "@eisox/design-system";
import { PencilIcon, PlusIcon } from "@eisox/icons";

import { FieldContainer } from "~/components/field-container";
import { NumberField } from "~/components/number-field";
import { DEFAULT_COMFORT_TEMPERATURE, DEFAULT_NIGHT_TEMPERATURE } from "~/constants/building.constant";
import { TEMPERATURE_MAX, TEMPERATURE_MIN, TEMPERATURE_STEP } from "~/constants/temperature.constant";
import { AvailableRoomTypes } from "~/helpers/room.helper";
import { clampTemperature, getRoomTemperatureBounds } from "~/helpers/temperature.helper";
import { m } from "~/paraglide/messages";
import type { RoomGroup } from "~/types/room.type";

import { checkGetName, type RoomFormDto, type RoomsDto } from "../helpers";

interface RoomFormProps extends ComponentPropsWithoutRef<"div"> {
  isCurrentRoomIsOnPlan: boolean;
  form: UseFormReturn<RoomFormDto, any, RoomFormDto>;
  handleUpdateRoom: SubmitHandler<RoomFormDto>;
  handleSetToNextRoom: () => void;
  handleDeleteRoom: (r: RoomFormDto) => void;
  fields: RoomsDto;
  building: Building;
  floors: Floor[];
  groups: RoomGroup[];
}

export const AddRoomForm: React.FC<RoomFormProps> = ({
  isCurrentRoomIsOnPlan,
  className,
  form,
  handleUpdateRoom,
  handleSetToNextRoom,
  handleDeleteRoom,
  fields,
  building,
  floors,
  groups,
  ...props
}) => {
  const {
    control,
    formState: { isDirty, errors },
    setValue,
    handleSubmit,
    reset,
    watch,
  } = form;

  const currentRoom = watch();
  const { comfortTemperatureBounds, nightTemperatureBounds } = getRoomTemperatureBounds({
    preComfortTemperature: building.preComfortTemperature,
  });

  const [showPattern, setShowPattern] = useState(false);

  const onNameChange = (room: RoomFormDto, takeRoomUid?: boolean) => {
    const uids = room.pattern
      ? (fields[room.pattern ? checkGetName(floors, room, false) : "default"]
          ?.map(f => f.uid)
          .filter((uid): uid is number => uid != null) ?? [])
      : [];
    const uid = takeRoomUid ? room.uid : uids.length ? Math.max(...uids) + 1 : 1;
    const name = checkGetName(floors, { ...room, uid });
    setValue("uid", uid);
    setValue("name", name);
  };

  return (
    <div {...props} className={cn("flex h-full w-[30%] flex-col gap-[20px]", className)}>
      <h3 className="font-semibold text-2xl">{isCurrentRoomIsOnPlan ? m.room_name() : m.new_room()}</h3>
      <div className="flex max-h-full flex-col gap-[20px] overflow-y-auto">
        <div className="flex items-center gap-[10px] *:w-1/2">
          <FieldContainer label={m.level()} error={errors.floor?.id}>
            <Controller
              control={control}
              name="floor.id"
              render={({ field: { onChange, value } }) => (
                <Select.Root
                  multiple={false}
                  value={value}
                  onValueChange={v => {
                    onChange(v);
                    onNameChange({ ...currentRoom, floor: { ...currentRoom.floor, id: v } });
                  }}
                >
                  <Select.Trigger>
                    <Select.Value placeholder={m.level()} />
                  </Select.Trigger>
                  <Select.Content>
                    <Select.Viewport>
                      {floors.map(p => (
                        <Select.Item key={p.id} value={p.id}>
                          {p.name || "--"}
                        </Select.Item>
                      ))}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Root>
              )}
            />
          </FieldContainer>
          <FieldContainer label={m.group()} error={errors.groupNames}>
            <Controller
              control={control}
              name="groupNames"
              render={({ field: { onChange, value } }) => (
                <Select.Root multiple value={value} onValueChange={onChange}>
                  <Select.Trigger>
                    <Select.Value placeholder={m.group()} />
                  </Select.Trigger>
                  <Select.Content>
                    <Select.Viewport>
                      {groups.map(g => (
                        <Select.Item key={g.name} value={g.name}>
                          {g.name}
                        </Select.Item>
                      ))}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Root>
              )}
            />
          </FieldContainer>
        </div>
        <div className="flex items-center gap-[10px] *:w-1/2">
          <FieldContainer label={m.type()} error={errors.type}>
            <Controller
              control={control}
              name="type"
              render={({ field: { value, onChange } }) => (
                <Select.Root
                  multiple={false}
                  value={value}
                  onValueChange={v => {
                    onChange(v);
                    onNameChange({ ...currentRoom, type: v as RoomType });
                  }}
                >
                  <Select.Trigger>
                    <Select.Value placeholder={m.select()} />
                  </Select.Trigger>
                  <Select.Content>
                    <Select.Viewport>
                      {AvailableRoomTypes.map(t => (
                        <Select.Item key={t} value={t}>
                          {m.room_type({ type: t })}
                        </Select.Item>
                      ))}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Root>
              )}
            />
          </FieldContainer>
          <FieldContainer label={m.index()} error={errors.uid}>
            <Controller
              control={control}
              name="uid"
              render={({ field: { value, onChange } }) => (
                <NumberField
                  onValueChange={v => {
                    onChange(v);
                    if (!isCurrentRoomIsOnPlan && v !== null) onNameChange({ ...currentRoom, uid: v }, true);
                  }}
                  value={value}
                  disabled={!currentRoom.pattern}
                  error={!!errors.uid}
                />
              )}
            />
          </FieldContainer>
        </div>
        <FieldContainer label={m.name()} error={errors.name}>
          <Controller
            control={control}
            name="name"
            render={({ field: { value, onChange } }) => (
              <TextField.Root
                value={value}
                onChange={v => {
                  onChange(v.target.value);
                  setValue("pattern", "");
                  setValue("uid", null);
                }}
              />
            )}
          />
        </FieldContainer>
        <Controller
          control={control}
          name="comfortTemperature"
          render={({ field: { value, onChange } }) => (
            <Slider
              label={m.comfort() + " (°C)"}
              value={value}
              defaultValue={DEFAULT_COMFORT_TEMPERATURE}
              min={TEMPERATURE_MIN}
              max={TEMPERATURE_MAX}
              step={TEMPERATURE_STEP}
              valueLabelFormat={`${comfortTemperatureBounds.min - TEMPERATURE_STEP}°C (${m.pre_comfort()}) < ${value}°C (${m.comfort()})`}
              onValueChange={nextValue => {
                if (typeof nextValue !== "number") return;
                onChange(clampTemperature(nextValue, comfortTemperatureBounds));
              }}
            />
          )}
        />
        {currentRoom.type === RoomType.ROOM && (
          <Controller
            control={control}
            name="nightTemperature"
            render={({ field: { value, onChange } }) => (
              <Slider
                label={m.night() + " (°C)"}
                value={value}
                defaultValue={DEFAULT_NIGHT_TEMPERATURE}
                min={TEMPERATURE_MIN}
                max={TEMPERATURE_MAX}
                step={TEMPERATURE_STEP}
                valueLabelDisplay="auto"
                valueLabelFormat={`${nightTemperatureBounds.min - TEMPERATURE_STEP}°C (${m.pre_comfort()}) < ${value}°C (${m.night()})`}
                onValueChange={value => {
                  if (typeof value === "number") onChange(clampTemperature(value, nightTemperatureBounds));
                }}
              />
            )}
          />
        )}

        <label className="font-medium flex flex-col gap-[5px] text-base text-primary-dark-gray">
          <p>{m.button_settings()}</p>
          <div className="flex items-center justify-between gap-[10px]">
            <p className="font-light text-primary-dark-blue italic">
              {m.activation({ activation: currentRoom.isSwitchEnabled ? "true" : "false" })}
            </p>
            <Controller
              control={control}
              name="isSwitchEnabled"
              render={({ field: { value, onChange } }) => <Switch checked={value} onCheckedChange={onChange} />}
            />
          </div>
        </label>
        <div className="flex flex-col gap-[10px]">
          <div className="flex items-center gap-[10px] [&_p]:text-base" onClick={() => setShowPattern(prev => !prev)}>
            <Radio checked={showPattern} multiple />
            <p>{m.advanced_configuration()}</p>
          </div>
          {showPattern && (
            <div className="[&_p]:text-sm [&_p]:whitespace-pre-line [&_p]:text-primary-dark-gray [&_p]:italic">
              <FieldContainer label={m.model()} error={errors.pattern}>
                <Controller
                  control={control}
                  name="pattern"
                  render={({ field: { value, onChange } }) => (
                    <TextField.Root
                      value={value}
                      onChange={e => {
                        onChange(e.target.value);
                        if (!e.target.value) return setValue("uid", null);
                        onNameChange({ ...currentRoom, pattern: e.target.value });
                        if (!e.target.value.includes("(index)")) setValue("uid", null);
                      }}
                    />
                  )}
                />
              </FieldContainer>
              <p>{m.add_room_model_help()}</p>
              <p>{`${m.level()}, ${m.type()}, ${m.index()}`}</p>
            </div>
          )}
        </div>
      </div>
      {isCurrentRoomIsOnPlan && (
        <div className="flex items-center justify-between gap-[10px]">
          <Button className="text-primary-red" onClick={() => handleDeleteRoom(currentRoom)}>
            {m.delete()}
          </Button>
          <div className="flex gap-[10px]">
            {isDirty && (
              <Button className="text-primary-dark-gray" onClick={() => reset()}>
                {m.cancel()}
              </Button>
            )}
            <ActionButton
              onClick={() => {
                void handleSubmit(handleUpdateRoom)();
              }}
              variant="primary"
              disabled={!isDirty}
            >
              {m.edit()} <PencilIcon />
            </ActionButton>
          </div>
        </div>
      )}

      <div
        className={cn(
          "mb-0 mx-auto mt-auto text-base [&_span]:text-primary-dark-gray",
          isDirty &&
            "[&_button]:text-primary-dark-gray [&_button_svg]:fill-primary-dark-gray [&_button_svg]:stroke-primary-dark-gray",
        )}
      >
        {isCurrentRoomIsOnPlan ? (
          <Button disabled={isDirty} onClick={handleSetToNextRoom}>
            {m.new_room()} <PlusIcon />
          </Button>
        ) : (
          <span>{m.click_on_plan_to_add_room()}</span>
        )}
      </div>
    </div>
  );
};
